import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Zap, FileDown, Copy, Pencil, Loader2, AlertTriangle, GitCompare, FileText, Maximize2, Minimize2, X, Trash2, Undo2, Redo2, Check, CloudOff, Signpost, ChevronDown, ClipboardList, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULTS, EVENT_TYPES, KINDS, STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import EventMap, { NoEntryMini } from "@/components/map/EventMap";
import { DrawToolbar, ModeHint } from "@/components/map/DrawToolbar";
import { MapLayersPanel } from "@/components/map/MapLayersPanel";
import { STAFF_GROUPS, StaffMini } from "@/components/map/StaffMarkers";
import { DetourPanel, StaffPanel } from "@/components/map/PickPanels";
import { TimeBar, staffAt } from "@/components/map/TimeBar";
import { MeasurePanel, BoxSavePanel, GroupPlacePanel } from "@/components/map/MapTools";
import { pathM } from "@/lib/geo";
import { inBox, offsetOf, applyOffset, simplify } from "@/lib/geo";
import { SIGNS, SignMini } from "@/components/map/SignObjects";
import { ElementForm } from "@/components/ElementForm";
import { ShiftsBriefing, defaultShifts, totalsFromShifts, shiftWindow } from "@/components/ShiftsBriefing";
import { AssignPanel } from "@/components/plan/AssignPanel";
import { buildUnits, staffPointsFrom, unitPosts, toMin, hm as hmOf, isMine } from "@/lib/staff";
import { EventInfoFields, DuplicateDialog } from "@/components/EventDialogs";
import { StaffGantt } from "@/components/plan/StaffGantt";
import { AiReport } from "@/components/plan/AiReport";
import { PlanSummary, DetoursList, SignageTable } from "@/components/plan/PlanParts";
import { TemplatesPanel, saveTemplate } from "@/components/TemplatesPanel";

const roadName = async ([lat, lon]) => {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=17&accept-language=es`);
    const j = await r.json();
    return j.address?.road || j.address?.pedestrian || "";
  } catch { return ""; }
};

const ElementList = ({ elements, selectedId, onSelect, timings }) => (
  <div className="space-y-1.5 max-h-[45vh] lg:max-h-none overflow-y-auto pr-1" data-testid="elements-list">
    {elements.length === 0 && <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-lg">Use las herramientas del mapa para marcar recorrido, cortes y zonas.</div>}
    {elements.map((e) => (
      <button key={e.id} onClick={() => onSelect(e.id)} data-testid={`element-item-${e.id}`}
        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${selectedId === e.id ? "border-sky-500/50 bg-sky-500/10" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}>
        {e.auto_generated ? <NoEntryMini /> : e.kind === "sign" ? <SignMini type={e.sign_type} rot={e.rotation} size={14} /> : <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: KINDS[e.kind].color }} />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-slate-800"><span className="truncate">{e.name || KINDS[e.kind].short}</span>
            {e.auto_generated && <span className={`shrink-0 text-[0.5625rem] px-1.5 rounded border ${e.locked ? "border-rose-400/60 text-rose-800" : "border-rose-400/30 text-rose-700"}`} data-testid={`element-auto-badge-${e.id}`}>{e.locked ? "Aceptado" : "Propuesta"}</span>}
          </span>
          <span className="block font-mono text-[0.625rem] text-slate-500">{e.kind === "sign" ? `${SIGNS[e.sign_type]?.code || "Objeto"}${SIGNS[e.sign_type]?.qty ? ` x${e.qty || 1}` : ""}` : KINDS[e.kind].short}{e.sign_dir === "uno" ? " · un sentido" : ""}{e.kind === "route" && ["sentido", "contrario"].includes(e.carriles) ? " · un carril" : ""}{timings?.[e.id] ? ` · ${timings[e.id].start_hm}–${timings[e.id].end_hm}` : ""}</span>
        </span>
      </button>
    ))}
  </div>
);

// Campos que deshacer/rehacer restauran (todo lo que el usuario edita; el plan se recalcula solo)
const HIST_FIELDS = ["name", "type", "date", "start_time", "end_time", "status", "location", "description", "town", "elements", "assignments",
  "shifts", "resources", "detour_overrides", "post_overrides", "auto_cuts", "auto_cuts_dismissed"];
const snapshot = (e) => Object.fromEntries(HIST_FIELDS.map((k) => [k, e[k]]));
const AUTOSAVE_MS = 1200;
const PLAN_ENGINE = 12; // igual que backend/planner.py: planes de un motor anterior se recalculan al abrir
// Plantilla de recuadro: todo lo dibujado que cae entero dentro (no el recorrido, ni propuestas sin aceptar, ni puestos de personal)
const TEMPLATE_DROP = ["id", "name", "coords", "group_id", "auto_generated", "auto_origin", "locked"]; // el nombre lleva la calle de origen: al colocarla se nombra con la plantilla
const boxContents = (ev, box) => {
  const usable = ev.elements.filter((e) => e.kind !== "route" && e.coords?.length && !(e.auto_generated && !e.locked));
  const inside = usable.filter((e) => e.coords.every((c) => inBox(c, box[0], box[1])));
  const partial = usable.filter((e) => !inside.includes(e) && e.coords.some((c) => inBox(c, box[0], box[1]))).length;
  const posts = (ev.assignments || []).filter((a) => a.loc && inBox(a.loc, box[0], box[1])).length;
  return { inside, partial, posts };
};
const COALESCE_MS = 800; // cambios seguidos (escribir, arrastrar) cuentan como un solo paso de deshacer
const MAX_HIST = 100;
const saveBody = (e) => { const b = { ...e }; delete b.plan; delete b.id; return b; };

const SyncBadge = ({ sync, onRetry }) => {
  if (sync.state === "saving") return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-700" data-testid="sync-badge"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>;
  if (sync.state === "planning") return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-700" data-testid="sync-badge"><Loader2 className="w-3 h-3 animate-spin" /> Recalculando plan…</span>;
  if (sync.state === "pending") return <span className="text-[0.625rem] px-2 py-0.5 rounded-full border border-slate-500/40 text-slate-500" data-testid="sync-badge">Cambios pendientes…</span>;
  if (sync.state === "error") return <button onClick={onRetry} className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-red-500/50 text-red-700 hover:bg-red-500/10" data-testid="sync-badge" title={sync.msg}><CloudOff className="w-3 h-3" /> No se pudo guardar · Reintentar</button>;
  if (sync.at) return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-700" data-testid="sync-badge"><Check className="w-3 h-3" /> Guardado {sync.at}</span>;
  return null;
};

export default function EventEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit } = useAuth();
  const [ev, setEv] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [rev, setRev] = useState(0);
  const [sync, setSync] = useState({ state: "idle" });
  const [hist, setHist] = useState({ undo: 0, redo: 0 });
  const evRef = useRef(null);
  const revRef = useRef(0);
  const dirtyRef = useRef(false);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const lastPushRef = useRef(0);
  const runningRef = useRef(false);
  const againRef = useRef(false);
  evRef.current = ev;
  dirtyRef.current = dirty;
  const [mode, setMode] = useState(null);
  const [assignKey, setAssignKey] = useState(null);
  const [focus, setFocus] = useState(null);
  const [draft, setDraft] = useState([]);
  const [sel, setSel] = useState(null);
  const [fit, setFit] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [dup, setDup] = useState(null);
  const [planTab, setPlanTab] = useState("summary");
  const [sideTab, setSideTab] = useState("elements");
  const [view, setView] = useState("map"); // «Mapa» para diseñar, «Plan» para revisar el resultado
  const [briefingNew, setBriefingNew] = useState(false);
  const [oneWays, setOneWays] = useState(false);
  const [base, setBase] = useState(() => localStorage.getItem("map_base_v2") || "light");
  const [hidden, setHidden] = useState([]);
  const [full, setFull] = useState(false);
  const [measure, setMeasure] = useState([]);
  const [box, setBox] = useState([]);
  const [group, setGroup] = useState(null);
  const [pick, setPick] = useState(null);
  const [at, setAt] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { localStorage.setItem("map_base_v2", base); }, [base]);
  const delRef = useRef(null);
  const routeReq = useRef({}); // última petición de trazado por desvío (si llegan desordenadas, gana la última)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Supr") && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) delRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  const [oneWayStatus, setOneWayStatus] = useState(null);
  const [sentFeedback, setSentFeedback] = useState([]);
  const loadFeedback = useCallback(() => { api.get(`/events/${id}/detour-feedback`).then((r) => setSentFeedback(r.data.map((x) => x.element_id))).catch(() => {}); }, [id]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      const d = r.data;
      if (!d.shifts?.length) { d.shifts = defaultShifts(d); setBriefingNew(true); setSideTab("personal"); }
      setEv(d); setFit((f) => f + 1);
      pastRef.current = []; futureRef.current = []; setHist({ undo: 0, redo: 0 });
      // plan pendiente o desfasado al abrir: se recalcula solo
      if (d.elements?.length && (!d.plan || d.updated_at > d.plan.generated_at || (d.plan.engine || 1) < PLAN_ENGINE)) setTimeout(() => syncRef.current?.(true), 300);
    }).catch((e) => { toast.error(errMsg(e)); nav("/"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  const markChanged = () => { revRef.current += 1; setRev(revRef.current); setDirty(true); setSync((s) => (s.state === "saving" || s.state === "planning" ? s : { ...s, state: "pending" })); };
  // Todo cambio del usuario pasa por aquí: guarda un paso de deshacer y programa el guardado automático
  const change = useCallback((fn, { history = true } = {}) => {
    const cur = evRef.current;
    if (history && cur) {
      const now = Date.now();
      if (now - lastPushRef.current > COALESCE_MS || !pastRef.current.length) {
        pastRef.current = [...pastRef.current.slice(-(MAX_HIST - 1)), snapshot(cur)];
      }
      lastPushRef.current = now;
      futureRef.current = [];
      setHist({ undo: pastRef.current.length, redo: 0 });
    }
    setEv((p) => fn(p));
    markChanged();
  }, []);
  const update = useCallback((patch) => change((p) => ({ ...p, ...patch })), [change]);
  const restore = (from, to) => {
    if (!from.current.length || !evRef.current) return;
    const target = from.current[from.current.length - 1];
    from.current = from.current.slice(0, -1);
    to.current = [...to.current, snapshot(evRef.current)];
    lastPushRef.current = 0;
    setEv((p) => ({ ...p, ...target }));
    setSel(null); setPick(null);
    setHist({ undo: pastRef.current.length, redo: futureRef.current.length });
    markChanged();
  };
  const undo = () => restore(pastRef, futureRef);
  const redo = () => restore(futureRef, pastRef);
  const undoRef = useRef(null);
  undoRef.current = { undo, redo };
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undoRef.current.undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); undoRef.current.redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Guardado automático + recálculo del plan. Si llegan cambios mientras trabaja, al terminar repite con lo último.
  const runSync = async (planOnly = false) => {
    if (!canEdit || !evRef.current) return;
    if (runningRef.current) { againRef.current = true; return; }
    runningRef.current = true;
    const startRev = revRef.current;
    try {
      if (!planOnly || dirtyRef.current) {
        setSync((s) => ({ ...s, state: "saving" }));
        const r = await api.put(`/events/${id}`, saveBody(evRef.current));
        setEv((p) => ({ ...p, updated_at: r.data.updated_at }));
        if (revRef.current === startRev) setDirty(false);
      }
      if (evRef.current.elements?.length) {
        setSync((s) => ({ ...s, state: "planning" }));
        const before = evRef.current.elements.filter((e) => e.auto_generated && !e.locked).length;
        const r = await api.post(`/events/${id}/plan`);
        if (revRef.current === startRev) {
          const fresh = await api.get(`/events/${id}`);
          setEv((p) => ({ ...p, plan: r.data, elements: fresh.data.elements, share_token: fresh.data.share_token, updated_at: fresh.data.updated_at }));
          const after = fresh.data.elements.filter((e) => e.auto_generated && !e.locked).length;
          if (after > before) toast.info(`${after - before} corte(s) previo(s) nuevo(s) propuesto(s) para revisar`);
        } else {
          setEv((p) => ({ ...p, plan: r.data, share_token: p.share_token || r.data.share_token }));
        }
        if (r.data.auto_cuts?.status === "no_disponible") toast.warning("No se pudo analizar la red viaria (OpenStreetMap no disponible): sin cortes previos automáticos");
      }
      setSync({ state: "idle", at: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      setSync({ state: "error", msg: errMsg(e) });
      toast.error(`No se pudo guardar: ${errMsg(e)}`);
    } finally {
      runningRef.current = false;
      if (againRef.current || revRef.current !== startRev) { againRef.current = false; if (dirtyRef.current) setTimeout(() => runSync(), 50); }
    }
  };
  const syncRef = useRef(null);
  syncRef.current = runSync;
  useEffect(() => {
    if (!dirty || !canEdit) return;
    const t = setTimeout(() => syncRef.current(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [rev, dirty, canEdit]);
  // Al salir con algo sin guardar: aviso del navegador y último guardado en segundo plano
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current || !evRef.current) return;
      fetch(`${API}/events/${id}`, { method: "PUT", credentials: "include", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify(saveBody(evRef.current)) }).catch(() => {});
    };
    const onBefore = (e) => { if (dirtyRef.current || runningRef.current) { flush(); e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => { window.removeEventListener("beforeunload", onBefore); flush(); };
  }, [id]);
  const setElements = (fn) => update({ elements: fn(ev.elements) });
  const buildGroup = (g) => g.tpl.items.map((it, i) => {
    const { offset, offsets, ...props } = it;
    const kind = it.kind || "sign";
    return {
      ...DEFAULTS[kind], ...props, kind, id: `${g.gid}-${i}`, group_id: g.gid,
      start_time: it.start_time || "", end_time: it.end_time || "",
      ...(kind === "sign" ? { qty: it.qty || 1, rotation: ((it.rotation || 0) + g.rot) % 360 } : {}),
      name: it.name || `${g.tpl.name} · ${kind === "sign" ? SIGNS[it.sign_type]?.label || "Objeto" : KINDS[kind]?.short}`,
      coords: (offsets || [offset]).map((o) => applyOffset(g.anchor, o, g.rot)),
    };
  });
  const placeGroup = (g) => {
    setGroup(g);
    if (g.anchor) update({ elements: [...ev.elements.filter((e) => e.group_id !== g.gid), ...buildGroup(g)] });
  };
  const cancelGroup = () => {
    if (group) update({ elements: ev.elements.filter((e) => e.group_id !== group.gid) });
    setGroup(null); setMode(null);
  };
  const saveBoxTemplate = async (name) => {
    const items = boxContents(ev, box).inside;
    const center = [(box[0][0] + box[1][0]) / 2, (box[0][1] + box[1][1]) / 2];
    try {
      await api.post("/templates", {
        kind: "sign_group", name, coords: [center],
        items: items.map((e) => ({
          ...Object.fromEntries(Object.entries(e).filter(([k]) => !TEMPLATE_DROP.includes(k))),
          offsets: e.coords.map((c) => offsetOf(center, c).map((v) => +v.toFixed(2))),
        })),
      });
      window.dispatchEvent(new Event("templates-changed"));
      toast.success(`Plantilla "${name}" guardada con ${items.length} elemento(s)`);
      setBox([]); setMode(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addFromTemplate = (t) => {
    if (t.kind === "sign_group") {
      // Se inserta exactamente donde estaba al guardarla; luego cada elemento se puede mover o editar
      const els = buildGroup({ tpl: t, gid: crypto.randomUUID(), anchor: t.coords[0], rot: 0 });
      update({ elements: [...ev.elements, ...els] });
      setFocus({ loc: t.coords[0], t: Date.now() });
      setSideTab("elements");
      toast.success(`«${t.name}» añadida en su ubicación original · ${els.length} elemento(s)`, { action: { label: "Deshacer", onClick: () => undoRef.current.undo() } });
      return;
    }
    const { id: _tid, created_by, created_at, ...props } = t;
    const existingRoute = t.kind === "route" && ev.elements.find((e) => e.kind === "route");
    const el = { ...props, id: existingRoute ? existingRoute.id : crypto.randomUUID() };
    if (t.kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    if (t.kind === "route") el.start_time = ev.start_time;
    setElements((els) => (existingRoute ? els.map((e) => (e.id === existingRoute.id ? el : e)) : [...els, el]));
    setSel(el.id);
    setSideTab("elements");
    toast.success(`"${t.name}" añadido al evento`);
  };



  const addElement = async (kind, coords, presetName, extra = {}) => {
    const existingRoute = kind === "route" && ev.elements.find((e) => e.kind === "route");
    if (existingRoute) {
      setElements((els) => els.map((e) => (e.id === existingRoute.id ? { ...e, coords } : e)));
      setSel(existingRoute.id);
      toast.info("Recorrido actualizado");
      return;
    }
    const newId = crypto.randomUUID();
    const n = ev.elements.filter((e) => e.kind === kind).length + 1;
    const el = { id: newId, kind, coords, name: presetName || `${KINDS[kind].short} ${n}`, start_time: kind === "route" ? ev.start_time : "", end_time: "", ...DEFAULTS[kind], ...extra };
    if (kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    change((p) => ({ ...p, elements: [...p.elements, el] }));
    setSel(newId);
    setSideTab("elements");
    if (kind !== "route" && !presetName) {
      const road = await roadName(kind === "street_cut" || kind === "special_path" || kind === "reversed_oneway" ? coords[Math.floor(coords.length / 2)] : coords[0]);
      if (road) change((p) => ({ ...p, elements: p.elements.map((e) => (e.id === newId && e.name === el.name ? { ...e, name: kind === "sign" ? `${SIGNS[e.sign_type]?.label || "Objeto"} · ${road}` : kind === "no_parking" ? `Zona ${road}` : kind === "street_cut" ? `Tramo ${road}` : kind === "special_path" ? `Paso especial ${road}` : kind === "reversed_oneway" ? `Sentido invertido ${road}` : `Corte ${road}` } : e)) }), { history: false });
    }
  };

  // Corte con dos clics: la app sigue la calle entre el inicio y el final y crea el tramo cortado
  const addCorte = async (a, b) => {
    setDraft([a, b]);
    let coords = [a, b];
    let name = "Corte";
    try {
      const r = await api.post("/tramo", { a, b });
      coords = r.data.coords;
      const c = r.data.calles;
      if (c.length) name = c.length > 1 ? `Corte ${c[0]} → ${c[c.length - 1]}` : `Corte ${c[0]}`;
    } catch (e) {
      toast.warning(`${errMsg(e)}. Se marca la línea recta entre los dos puntos: ajústela si hace falta.`);
    }
    addElement("street_cut", coords, name);
    setDraft([]); setMode(null);
  };

  const onMapClick = (p) => {
    if (mode === "assign") { addPost(p); return; }
    if (mode === "corte") { if (draft.length) addCorte(draft[0], p); else setDraft([p]); return; }
    if (mode === "cut_point") { addElement("cut_point", [p]); setMode(null); return; }
    if (mode === "measure") { setMeasure((m) => [...m, p]); return; }
    if (mode === "box") { setBox((b) => (b.length >= 2 ? [p] : [...b, p])); return; }
    if (mode === "place_group" && group) { placeGroup({ ...group, anchor: p }); return; }
    if (mode?.startsWith("sign:")) {
      const t = mode.slice(5);
      addElement("sign", [p], undefined, { sign_type: t, name: `${SIGNS[t].label} ${ev.elements.filter((e) => e.kind === "sign").length + 1}` });
      return;
    }
    setDraft((d) => [...d, p]);
  };
  const finishDraw = () => {
    if (mode === "sign_line") addElement("sign", draft, undefined, { sign_type: "valla_linea", spacing: 2.5, name: `Línea de vallas ${ev.elements.filter((e) => e.sign_type === "valla_linea").length + 1}` });
    else addElement(mode, draft);
    setDraft([]); setMode(null);
  };
  const cancelDraw = () => { setDraft([]); setMode(null); };
  const pickOneWay = (w) => {
    addElement("reversed_oneway", [...w.coords].reverse().map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]), w.name ? `Sentido invertido ${w.name}` : undefined);
    setMode(null);
    toast.success("Sentido invertido añadido · ajuste el tramo y su horario");
  };

  const generate = () => runSync(!dirty);

  if (!ev) return <div className="p-10 font-mono text-xs text-slate-500" data-testid="editor-loading">CARGANDO EVENTO…</div>;

  const plan = ev.plan;
  const syncing = sync.state === "saving" || sync.state === "planning";
  const stale = plan && (dirty || syncing || ev.updated_at > plan.generated_at);
  const selected = ev.elements.find((e) => e.id === sel);
  const proposals = ev.elements.filter((e) => e.auto_generated && !e.locked);
  const dov = ev.detour_overrides || {};
  // desvíos editados a mano: el principal (clave = id del corte) y cada rama (clave = «id#n»); formato antiguo = lista de puntos
  const ovOf = (k) => { const o = dov[k]; return !o ? null : Array.isArray(o) ? { coords: o, waypoints: o, streets: [] } : o; };
  const shownDetours = (plan?.detours || []).filter((d) => ev.elements.some((e) => e.id === d.element_id)).map((d) => {
    const m = ovOf(d.element_id);
    const main = m ? { ...d, coords: m.coords, waypoints: m.waypoints, libre: m.libre, manual: true, distance_m: Math.round(pathM(m.coords)), duration_min: +(pathM(m.coords) / 416).toFixed(1), streets: m.streets || [], entry_point: m.coords[0] } : d;
    return { ...main, branches: (d.branches || []).map((b, i) => { const o = ovOf(`${d.element_id}#${i}`); return o ? { ...b, coords: o.coords, waypoints: o.waypoints, libre: o.libre, manual: true, streets: o.streets || [], distance_m: Math.round(pathM(o.coords)) } : b; }) };
  });
  // una línea de desvío por clave: la principal o una rama, con los datos del corte para el panel
  const lineOf = (key) => {
    const [eid, n] = String(key).split("#");
    const d = shownDetours.find((x) => x.element_id === eid);
    if (!d) return null;
    if (n === undefined) return { ...d, key: eid };
    const b = d.branches?.[+n];
    return b ? { ...d, ...b, key, branch: true, branches: [], distance_m: b.distance_m, duration_min: +((b.distance_m || 0) / 416).toFixed(1), entry_point: b.coords?.[0] } : null;
  };
  const routeLine = async (key, waypoints, libre) => {
    const eid = String(key).split("#")[0];
    const req = (routeReq.current[key] || 0) + 1;
    routeReq.current[key] = req;
    update({ detour_overrides: { ...dov, [key]: { ...(ovOf(key) || {}), waypoints, libre } } });
    try {
      const r = await api.post(`/events/${id}/desvio-ruta`, { waypoints, element_id: eid, libre: !!libre });
      if (routeReq.current[key] !== req) return;
      change((p) => ({ ...p, detour_overrides: { ...(p.detour_overrides || {}), [key]: { waypoints, libre: !!libre, coords: r.data.coords, streets: r.data.streets } } }), { history: false });
      if (r.data.tramos_rectos?.length) toast.warning(`Entre ${r.data.tramos_rectos.map((i) => `${i + 1} y ${i + 2}`).join(", ")} no hay camino por calles abiertas: el tramo queda recto. Mueva los nodos.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const editLine = (l) => {
    // nodos iniciales: el trazado simplificado (inicio, giros y final), como mucho 10
    let w = simplify(l.coords, 12);
    for (let tol = 25; w.length > 10; tol *= 2) w = simplify(l.coords, tol);
    update({ detour_overrides: { ...dov, [l.key]: { waypoints: w.map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]), coords: l.coords, streets: l.streets || [] } } });
  };
  const units = buildUnits(ev.shifts || []);
  const anchor = (toMin(ev.start_time) ?? 600) - 360;
  const assignments = ev.assignments || [];
  const allStaff = staffPointsFrom(units, assignments, anchor);
  const shownStaff = staffAt(allStaff, at);
  const tlist = plan?.tasks || [];
  const slotsAll = allStaff.flatMap((p) => p.slots);
  const trange = slotsAll.length ? [Math.floor(Math.min(...slotsAll.map((s) => s.start)) / 5) * 5, Math.ceil(Math.max(...slotsAll.map((s) => s.end)) / 5) * 5 - 5] : null;
  const assignUnit = units.find((u) => u.key === assignKey);
  const unitPostsSel = assignUnit ? unitPosts(assignUnit, assignments, anchor, units) : [];
  const addPost = (p) => {
    if (!assignUnit) return;
    const mine = assignments.filter((a) => isMine(a, assignUnit, units));
    const near = ev.elements.filter((e) => e.coords?.length && e.kind !== "route").map((e) => ({ e, d: Math.min(...e.coords.map((c) => Math.hypot((c[0] - p[0]) * 110540, (c[1] - p[1]) * 82000))) }))
      .filter((x) => x.d < 60).sort((a, b) => a.d - b.d)[0];
    const a = { id: crypto.randomUUID(), unit_key: assignUnit.key, unit_id: assignUnit.id, loc: p, time: mine.length ? "" : hmOf(assignUnit.shiftFrom), place: near ? near.e.name : "", notes: "" };
    update({ assignments: [...assignments, a] });
    setMode(null);
    toast.success(`Ubicación ${mine.length + 1} asignada a ${assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id} · indique la hora y las órdenes`);
    if (!near) roadName(p).then((road) => road && change((x) => ({ ...x, assignments: (x.assignments || []).map((y) => (y.id === a.id && !y.place ? { ...y, place: road } : y)) }), { history: false }));
  };
  const selectEl = (id) => { setSel(id); setPick(null); if (id) setSideTab("elements"); };
  const pickDetour = (id) => { setSel(null); setPick({ type: "detour", id }); setSideTab("elements"); };
  const pickStaff = (key) => {
    setSel(null); setPick({ type: "staff", id: key }); setSideTab("elements");
    const a = assignments.find((x) => x.id === key);
    const o = a && units.find((u) => isMine(a, u, units));
    if (o) setAssignKey(o.key);
  };
  const pDetour = pick?.type === "detour" && lineOf(pick.id);
  const pStaff = pick?.type === "staff" && (shownStaff.find((p) => p.key === pick.id) || allStaff.find((p) => p.key === pick.id));
  const pickPanel = pDetour ? (
    <DetourPanel d={pDetour} edited={!!dov[pDetour.key]} canEdit={canEdit} eventId={id} sent={sentFeedback.includes(pDetour.element_id)} onSent={loadFeedback}
      onEdit={() => editLine(pDetour)}
      onReset={() => { const n = { ...dov }; delete n[pDetour.key]; update({ detour_overrides: n }); }}
      onLibre={(v) => pDetour.waypoints && routeLine(pDetour.key, pDetour.waypoints, v)}
      onPickBranch={(k) => pickDetour(k)}
      onOpenCut={() => selectEl(pDetour.element_id)} />
  ) : pStaff ? (
    <StaffPanel p={pStaff} at={at} moved={false} canEdit={canEdit} onReset={() => {}}
      onOpenTask={(i) => { const a = assignments.find((x) => pStaff.task_ids.includes(x.id) && (x.place || "Puesto") === pStaff.tasks[i]); const o = a && units.find((u) => isMine(a, u, units)); if (o) { setAssignKey(o.key); setSideTab("personal"); setTimeout(() => document.querySelector("[data-testid=assign-panel]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); } }} />
  ) : null;
  const moveStaff = (key, loc) => {
    const p = allStaff.find((x) => x.key === key);
    if (!p) return;
    // with a unit selected, move only its post; otherwise the whole group standing there
    const own = assignUnit && p.task_ids.filter((t) => unitPostsSel.some((x) => x.id === t));
    const ids = own?.length ? own : p.task_ids;
    update({ assignments: assignments.map((a) => (ids.includes(a.id) ? { ...a, loc } : a)) });
  };
  const timings = plan?.timings || {};
  const deleteSelected = () => {
    if (!selected) return;
    update({ elements: ev.elements.filter((e) => e.id !== selected.id), ...(selected.auto_generated ? { auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), selected.auto_origin || selected.coords[0]] } : {}) });
    setSel(null);
    toast.success(`«${selected.name || "Elemento"}» eliminado`, { action: { label: "Deshacer", onClick: () => undoRef.current.undo() } });
  };
  delRef.current = canEdit && !mode ? deleteSelected : null;
  const selDetour = selected && plan?.detours?.find((d) => d.element_id === selected.id);
  const elementForm = selected && (
    <>
    {selDetour && !selDetour.coords?.length && selDetour.note && (
      <div className="mb-3 text-[0.6875rem] text-amber-800 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5" data-testid="element-detour-note">{selDetour.note}</div>
    )}
    {selDetour?.coords?.length > 0 && (
      <button onClick={() => pickDetour(selected.id)} className="mb-3 w-full text-left text-[0.6875rem] text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 hover:bg-emerald-500/20 transition-colors" data-testid="element-detour-link">
        Desvío: {selDetour.distance_m} m · {selDetour.streets.slice(0, 4).join(" → ")}{selDetour.streets.length > 4 ? "…" : ""}
      </button>
    )}
    <ElementForm key={selected.id} el={selected} readOnly={!canEdit} timing={stale ? null : timings[selected.id]}
      onChange={(n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e)))}
      onDelete={() => deleteSelected()}
      onSaveTemplate={canEdit ? () => saveTemplate(selected) : undefined} />
    </>
  );

  const legend = {
    route: <span className="w-3 h-1.5 rounded-full" style={{ background: KINDS.route.color }} />,
    cuts: <span className="w-2.5 h-2.5 rounded-full" style={{ background: KINDS.cut_point.color }} />,
    autocuts: <NoEntryMini />,
    no_parking: <span className="w-2.5 h-2.5 rounded-sm" style={{ background: KINDS.no_parking.color }} />,
    special: <span className="w-2.5 h-2.5 rounded-full" style={{ background: KINDS.special_path.color }} />,
    signs: <SignMini type="r101" size={13} />,
    detours: <span className="w-3 h-1.5 rounded-full bg-green-500" />,
    staff: <span className="flex -space-x-1">{Object.keys(STAFF_GROUPS).map((k) => <StaffMini key={k} group={k} size={11} />)}</span>,
  };
  const deficit = !stale && plan?.summary?.deficit_count;
  const toolMode = mode && !["measure", "box", "place_group", "assign"].includes(mode);

  return (
    <div className="lg:h-screen flex flex-col min-h-0" data-testid="event-editor">
      {/* Barra del evento: datos, deshacer, cambio Mapa / Plan y documentos */}
      <header className="shrink-0 bg-white border-b border-slate-200 px-3 sm:px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Link to="/" title="Archivo de eventos" className="p-2 -ml-1 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-sky-700" data-testid="back-to-events-link"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="min-w-0 flex-1 basis-48">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight truncate" data-testid="event-title">{ev.name}</h1>
            <span className={`shrink-0 text-[0.625rem] px-2 py-0.5 rounded-full border ${STATUS[ev.status]?.cls}`} data-testid="event-status-badge">{STATUS[ev.status]?.label}</span>
            {canEdit && <span className="shrink-0"><SyncBadge sync={sync} onRetry={() => runSync()} /></span>}
          </div>
          <div className="font-mono text-[0.6875rem] text-slate-500 truncate">{EVENT_TYPES[ev.type]} · {ev.date} · {ev.start_time}–{ev.end_time} · <b className="text-sky-700" data-testid="event-town-label">{ev.town?.name || "Sin población"}</b>{ev.location ? ` · ${ev.location}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={undo} disabled={!hist.undo} title="Deshacer (Cmd/Ctrl+Z)" data-testid="undo-button" className="h-9 px-2.5 text-slate-700 hover:bg-slate-100 disabled:opacity-35"><Undo2 className="w-4 h-4" /></button>
              <button onClick={redo} disabled={!hist.redo} title="Rehacer (Cmd/Ctrl+Shift+Z)" data-testid="redo-button" className="h-9 px-2.5 text-slate-700 hover:bg-slate-100 disabled:opacity-35 border-l border-slate-200"><Redo2 className="w-4 h-4" /></button>
            </div>
          )}
          {canEdit && <Button variant="outline" className="h-9 px-3" onClick={() => { setInfo({ ...ev }); setInfoOpen(true); }} title="Datos del evento" data-testid="edit-event-info-button"><Pencil className="w-4 h-4 md:mr-1.5" /><span className="hidden md:inline">Datos</span></Button>}
          <div className="flex rounded-lg bg-slate-100 p-0.5" role="tablist" data-testid="view-switch">
            {[["map", MapIcon, "Mapa"], ["plan", ClipboardList, "Plan"]].map(([v, I, l]) => (
              <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`relative h-8 px-3 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
                <I className="w-4 h-4" />{l}
                {v === "plan" && (syncing ? <Loader2 className="w-3 h-3 animate-spin text-sky-600" /> : deficit ? <span className="text-[0.625rem] font-bold px-1.5 rounded-full bg-amber-400 text-slate-950" title={`${deficit} tareas sin cubrir`}>{deficit}</span> : null)}
              </button>
            ))}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white" data-testid="documents-menu-button"><FileText className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Documentos</span><ChevronDown className="w-4 h-4 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[0.625rem] font-mono tracking-widest text-slate-500">PARA IMPRIMIR Y REPARTIR</DropdownMenuLabel>
              <DropdownMenuItem disabled={!plan} onSelect={() => window.open(`/events/${id}/sheets`, "_blank")} data-testid="service-sheets-button"><FileText className="w-4 h-4 mr-2" /> Hojas de servicio</DropdownMenuItem>
              <DropdownMenuItem disabled={!plan} onSelect={() => window.open(`/events/${id}/print`, "_blank")} data-testid="export-pdf-plan-button"><FileDown className="w-4 h-4 mr-2" /> Plan completo en PDF</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => window.open(`/events/${id}/carteles`, "_blank")} data-testid="carteles-button"><Signpost className="w-4 h-4 mr-2" /> Cartelería</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[0.625rem] font-mono tracking-widest text-slate-500">EVENTO</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => nav(`/events/${id}/compare`)} data-testid="compare-editions-button"><GitCompare className="w-4 h-4 mr-2" /> Comparar ediciones</DropdownMenuItem>
              {canEdit && <DropdownMenuItem onSelect={() => setDup(ev)} data-testid="duplicate-event-button"><Copy className="w-4 h-4 mr-2" /> Duplicar para otro año</DropdownMenuItem>}
              {canEdit && <DropdownMenuItem disabled={syncing || !ev.elements.length} onSelect={generate} data-testid="generate-traffic-plan-button"><Zap className="w-4 h-4 mr-2" /> Recalcular el plan ahora</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className={view === "map" ? "flex-1 min-h-0 flex flex-col lg:flex-row" : "hidden"}>
        <div className={full ? "fixed inset-0 z-[60] bg-slate-100 isolate" : "relative isolate z-0 h-[68dvh] min-h-[22rem] lg:h-auto lg:flex-1 lg:min-w-0"} data-testid="event-map">
          <DrawToolbar readOnly={!canEdit} mode={mode} setMode={(m) => { if (mode === "place_group") cancelGroup(); setMode(m); setDraft([]); setBox([]); setMeasure([]); if (m === "reversed_oneway") setOneWays(true); }}
            measureActive={mode === "measure"} onMeasure={() => { setMeasure([]); setDraft([]); if (mode === "place_group") cancelGroup(); setMode(mode === "measure" ? null : "measure"); }} />
          <EventMap elements={ev.elements} detours={shownDetours} timings={timings} selectedId={sel} onSelect={selectEl}
            onSelectDetour={pickDetour} selectedDetour={pDetour ? pDetour.key : null}
            onDetourChange={canEdit ? (key, w) => routeLine(key, w, ovOf(key)?.libre) : undefined}
            onSelectStaff={pickStaff} selectedStaff={pStaff ? pStaff.key : null} onMoveStaff={canEdit ? moveStaff : undefined}
            drawMode={mode} draft={draft} onMapClick={onMapClick} fitTrigger={fit}
            onElementChange={canEdit ? (n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e))) : undefined}
            showOneWays={oneWays} onOneWayStatus={setOneWayStatus} onPickOneWay={pickOneWay} base={base} hidden={hidden} resizeKey={`${full}-${view}`} measure={measure} box={box} center={ev.town ? [ev.town.lat, ev.town.lng] : undefined} staff={shownStaff}
            posts={unitPostsSel} focus={focus} zoomPosition="bottomright" />
          {trange && !mode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[550]">
              <TimeBar range={trange} at={at} setAt={setAt} playing={playing} setPlaying={setPlaying} />
            </div>
          )}
          <div className={`absolute top-3 z-[600] flex items-start gap-2 ${full && (selected || pickPanel) ? "right-[376px]" : "right-3"}`}>
            <MapLayersPanel base={base} setBase={setBase} hidden={hidden} setHidden={setHidden} legend={legend} oneWays={oneWays} setOneWays={setOneWays} oneWayStatus={oneWayStatus} />
            <button onClick={() => setFull((v) => !v)} data-testid="map-fullscreen-button" title={full ? "Salir de pantalla completa (Esc)" : "Pantalla completa"}
              className="h-9 w-9 grid place-items-center rounded-lg border shadow-sm backdrop-blur-md bg-white/95 text-slate-700 border-slate-200 hover:bg-slate-100">
              {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
          {/* abajo en el centro: lo que hay que hacer ahora en el mapa */}
          {canEdit && toolMode && <ModeHint mode={mode} draft={draft} onFinish={finishDraw} onCancel={cancelDraw} onUndo={() => setDraft((d) => d.slice(0, -1))} />}
          {mode === "measure" && <MeasurePanel pts={measure} onUndo={() => setMeasure((m) => m.slice(0, -1))} onClear={() => setMeasure([])} onClose={() => { setMeasure([]); setMode(null); }} />}
          {mode === "box" && <BoxSavePanel corners={box} contents={box.length === 2 ? boxContents(ev, box) : null} onSave={saveBoxTemplate} onCancel={() => { setBox([]); setMode(null); }} />}
          {mode === "place_group" && group && <GroupPlacePanel tpl={group.tpl} placed={!!group.anchor} rot={group.rot} setRot={(rot) => placeGroup({ ...group, rot })}
            onDone={() => { toast.success(`${group.tpl.items.length} objeto(s) colocados`); setGroup(null); setMode(null); }} onCancel={cancelGroup} />}
          {mode === "assign" && assignUnit && (
            <div className="map-hint border-amber-400 bg-amber-50" data-testid="assign-map-hint">
              <span className="text-xs font-semibold text-slate-900">Pulse en el mapa la ubicación de {assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id}</span>
              <button onClick={() => setMode(null)} className="map-hint-btn">Cancelar</button>
            </div>
          )}
          {canEdit && selected && !mode && !full && (
            <div className="map-hint border-sky-500/30" data-testid="vertex-edit-hint">
              <span className="min-w-0 text-[0.6875rem] text-slate-600"><b className="text-slate-900">{selected.name}</b> · {selected.kind === "cut_point" || (selected.kind === "sign" && selected.sign_type !== "valla_linea") ? "arrastre el punto para moverlo" : "arrastre los cuadrados blancos para mover, los azules para añadir punto; clic derecho borra"}</span>
              <button onClick={deleteSelected} title="Eliminar (tecla Supr)" data-testid="map-delete-selected-button"
                className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10 text-red-700 hover:bg-red-500/20 text-[0.6875rem] font-semibold"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
              <button onClick={() => setSel(null)} title="Cerrar" className="shrink-0 p-1 text-slate-500 hover:text-slate-900" data-testid="map-deselect-button"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {full && (selected || pickPanel) && (
            <div className="absolute top-3 right-3 bottom-6 w-[360px] z-[600] bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-4 overflow-y-auto rise" data-testid="fullscreen-element-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[0.625rem] tracking-widest text-sky-600">ELEMENTO</span>
                <button onClick={() => { setSel(null); setPick(null); }} className="text-slate-500 hover:text-slate-900" data-testid="fullscreen-panel-close"><X className="w-4 h-4" /></button>
              </div>
              {elementForm || pickPanel}
            </div>
          )}
        </div>

        {/* Panel lateral: lo dibujado, el personal y las plantillas */}
        <aside className="bg-white border-t lg:border-t-0 lg:border-l border-slate-200 lg:w-[25rem] xl:w-[28rem] shrink-0 flex flex-col min-h-0" data-testid="side-panel">
          <Tabs value={sideTab} onValueChange={setSideTab} className="flex flex-col min-h-0 flex-1">
            <div className="shrink-0 px-3 pt-3 pb-2 border-b border-slate-100">
              <TabsList className="w-full bg-slate-100">
                <TabsTrigger value="elements" className="flex-1" data-testid="tab-elements">Elementos ({ev.elements.length})</TabsTrigger>
                <TabsTrigger value="personal" className="flex-1" data-testid="tab-briefing">Personal ({units.length})</TabsTrigger>
                <TabsTrigger value="templates" className="flex-1" data-testid="tab-templates">Plantillas</TabsTrigger>
              </TabsList>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
              <TabsContent value="elements" className="mt-0 space-y-4">
                {selected || pickPanel ? (
                  <div data-testid="element-detail">
                    <button onClick={() => { setSel(null); setPick(null); }} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-700" data-testid="element-back-button">
                      <ArrowLeft className="w-3.5 h-3.5" /> Todos los elementos
                    </button>
                    {!full ? (elementForm || pickPanel) : <div className="text-xs text-slate-500">Editando en pantalla completa.</div>}
                  </div>
                ) : (
                  <>
                    <label className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="auto-cuts-toggle-row">
                      <span><span className="text-xs text-slate-800 block">Cortes previos automáticos</span>
                        <span className="text-[0.625rem] text-slate-500">Valla el último cruce antes de cada calle afectada</span></span>
                      <Switch checked={ev.auto_cuts !== false} disabled={!canEdit} onCheckedChange={(v) => update({ auto_cuts: v })} data-testid="auto-cuts-switch" />
                    </label>
                    {proposals.length > 0 && (
                      <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5 space-y-2" data-testid="auto-cuts-review">
                        <div className="text-xs text-rose-800"><b>{proposals.length}</b> cortes previos propuestos. Revíselos en el mapa: solo cuentan en el plan cuando los acepta.</div>
                        {canEdit && (
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 bg-emerald-500 hover:bg-emerald-400 text-slate-950" onClick={() => setElements((els) => els.map((e) => (e.auto_generated && !e.locked ? { ...e, locked: true } : e)))} data-testid="auto-cuts-accept-all-button">Aceptar todos</Button>
                            <Button size="sm" variant="outline" className="h-7 text-red-700" onClick={() => { update({ elements: ev.elements.filter((e) => !(e.auto_generated && !e.locked)), auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), ...proposals.map((e) => e.auto_origin || e.coords[0])] }); setSel(null); }} data-testid="auto-cuts-reject-all-button">Rechazar todos</Button>
                          </div>
                        )}
                      </div>
                    )}
                    <ElementList elements={ev.elements} selectedId={sel} onSelect={selectEl} timings={stale ? null : timings} />
                    {ev.elements.length > 0 && <div className="text-[0.6875rem] text-slate-500">Pulse un elemento (aquí o en el mapa) para editar su horario, su personal y sus opciones.</div>}
                  </>
                )}
              </TabsContent>
              <TabsContent value="personal" className="mt-0 space-y-5">
                <ShiftsBriefing eventId={id} shifts={ev.shifts} isNew={briefingNew} readOnly={!canEdit} summary={stale ? null : plan?.summary} window={ev.start_time && ev.end_time ? shiftWindow(ev) : null}
                  onChange={(s) => { setBriefingNew(false); update({ shifts: s, resources: totalsFromShifts(s) }); }} />
                <AssignPanel units={units} assignments={assignments} anchor={anchor} selKey={assignKey} setSelKey={setAssignKey}
                  armed={mode === "assign"} setArmed={(v) => { setDraft([]); setMode(v ? "assign" : null); }} canEdit={canEdit}
                  onChange={(a) => update({ assignments: a })} onFocus={(loc) => setFocus({ loc, t: Date.now() })} />
              </TabsContent>
              <TabsContent value="templates" className="mt-0">
                <TemplatesPanel canEdit={canEdit} elements={ev.elements} onAdd={addFromTemplate} />
              </TabsContent>
            </div>
          </Tabs>
        </aside>
      </div>

      {view === "plan" && (
      <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5">
      <section id="plan-section" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 max-w-7xl mx-auto" data-testid="plan-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-xs tracking-[0.25em] text-sky-600">PLAN DE TRÁFICO</div>
            <h2 className="font-display text-2xl font-bold">Propuesta operativa</h2>
          </div>
          <div className="flex items-center gap-3">
            {plan && <div className="font-mono text-[0.6875rem] text-slate-500">Generado {new Date(plan.generated_at).toLocaleString("es-ES")}</div>}
            <Button variant="outline" size="sm" onClick={() => setView("map")} data-testid="plan-back-to-map"><MapIcon className="w-4 h-4 mr-1.5" /> Volver al mapa</Button>
          </div>
        </div>
        {stale && (sync.state === "error"
          ? <div className="flex items-center gap-2 text-sm text-red-700 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 mb-4" data-testid="plan-stale-warning"><AlertTriangle className="w-4 h-4" /> No se pudo guardar ni recalcular. <button className="underline" onClick={() => runSync()}>Reintentar</button></div>
          : <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded-lg px-4 py-2 mb-4" data-testid="plan-stale-warning"><Loader2 className="w-4 h-4 animate-spin" /> Actualizando el plan con los últimos cambios…</div>)}
        {!plan ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-10 text-center" data-testid="plan-empty">
            Marque en el mapa el recorrido, los puntos de corte, tramos y zonas sin estacionamiento e indique el personal disponible: el plan se calcula solo con cada cambio.
          </div>
        ) : (
          <Tabs value={planTab} onValueChange={setPlanTab}>
            <TabsList className="bg-slate-50 flex-wrap h-auto">
              <TabsTrigger value="summary" data-testid="plan-tab-summary">Resumen</TabsTrigger>
              <TabsTrigger value="detours" data-testid="plan-tab-detours">Desvíos ({plan.detours.length})</TabsTrigger>
              <TabsTrigger value="staff" data-testid="plan-tab-staff">Cronograma de personal</TabsTrigger>
              <TabsTrigger value="signage" data-testid="plan-tab-signage">Señalización y estacionamiento</TabsTrigger>
              <TabsTrigger value="report" data-testid="plan-tab-report">Orden de servicio (IA)</TabsTrigger>
            </TabsList>
            <TabsContent value="summary" className="mt-5"><PlanSummary plan={plan} elements={ev.elements} /></TabsContent>
            <TabsContent value="detours" className="mt-5"><DetoursList detours={plan.detours} eventId={id} canEdit={canEdit} overrides={ev.detour_overrides || {}} sentIds={sentFeedback} onSent={loadFeedback} /></TabsContent>
            <TabsContent value="staff" className="mt-5"><StaffGantt units={plan.units} eventId={id} shareToken={ev.share_token} /></TabsContent>
            <TabsContent value="signage" className="mt-5"><SignageTable signage={plan.signage} parking={plan.parking} /></TabsContent>
            <TabsContent value="report" className="mt-5">
              <AiReport eventId={id} text={plan.ai_report} canEdit={canEdit} stale={stale} onDone={(t) => setEv((p) => ({ ...p, plan: { ...p.plan, ai_report: t } }))} />
            </TabsContent>
          </Tabs>
        )}
      </section>
      </div>
      )}

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle className="font-display">Datos del evento</DialogTitle></DialogHeader>
          {info && <EventInfoFields value={info} onChange={setInfo} withStatus />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInfoOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-event-apply-button" onClick={() => { const { name, type, date, start_time, end_time, status, location, description, town } = info; update({ name, type, date, start_time, end_time, status, location, description, town }); setInfoOpen(false); }}>Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateDialog source={dup} onClose={() => setDup(null)} onDone={(n) => nav(`/events/${n.id}`)} />
    </div>
  );
}
